// Global Data Refresh Manager - Server-Side Coordination
interface RefreshStatus {
  isRefreshing: boolean;
  lastRefresh: Date | null;
  nextRefresh: Date | null;
  intervalMinutes: number;
  initiated_by?: number;
}

class GlobalDataRefreshManager {
  private static instance: GlobalDataRefreshManager | null = null;
  private statusPollTimer: ReturnType<typeof setInterval> | null = null; // short-lived polling during active refresh
  private scheduledCheckTimer: ReturnType<typeof setTimeout> | null = null; // next check scheduled (1 min after next_refresh)
  private listeners: Set<() => void> = new Set();
  private currentStatus: RefreshStatus = {
    isRefreshing: false,
    lastRefresh: null,
    nextRefresh: null,
    intervalMinutes: 30
  };
  private authCheckFunction: (() => boolean) | null = null;
  private isDuringActivePoll = false;
  private authLogoutListener: (() => void) | null = null;
  // New: suspend all activity after an auth error until a real login occurs
  private isSuspendedOnAuthError = false;
  // Persist suspension across reloads to avoid redirect loops
  private static readonly SUSPEND_STORAGE_KEY = 'gdrm:suspended';

  private constructor() {
    // Do not start any intervals here; wait for onLogin trigger
    // Listen for auth-logout events to immediately stop all operations
    this.setupAuthLogoutListener();

    // Read persisted suspension state (sessionStorage) to avoid loops after reload
    if (typeof window !== 'undefined') {
      try {
        const v = window.sessionStorage.getItem(GlobalDataRefreshManager.SUSPEND_STORAGE_KEY);
        this.isSuspendedOnAuthError = v === '1' || v === 'true';
      } catch {
        // ignore storage errors
      }
    }
  }

  // Listen for auth-logout events to immediately stop all operations
  private setupAuthLogoutListener(): void {
    if (typeof window === 'undefined') return;

    this.authLogoutListener = () => {
      this.clearTimers();
      // Reset status to default state
      this.currentStatus = {
        isRefreshing: false,
        lastRefresh: null,
        nextRefresh: null,
        intervalMinutes: 30
      };
      this.isDuringActivePoll = false;
      // On explicit logout, do not keep suspended flag set; user intentionally logged out
      this.setSuspended(false);
    };

    window.addEventListener('auth-logout', this.authLogoutListener);
  }

  public static getInstance(): GlobalDataRefreshManager {
    if (!GlobalDataRefreshManager.instance) {
      GlobalDataRefreshManager.instance = new GlobalDataRefreshManager();
    }
    return GlobalDataRefreshManager.instance;
  }

  // Subscribe to refresh events
  public onRefresh(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Get refresh status
  public getStatus(): RefreshStatus {
    return { ...this.currentStatus };
  }

  // Set authentication check function (should return true only for authenticated admin)
  public setAuthCheck(authCheckFn: () => boolean): void {
    this.authCheckFunction = authCheckFn;
  }

  // Notify manager that a login occurred (or auth became true)
  public async onLogin(): Promise<void> {
    if (!this.isAuthenticated()) return;
    // Cancel prior timers if any
    this.clearTimers();
    // A successful login should lift any suspension due to prior 401s
    this.setSuspended(false);
    // Initial status fetch and one-time check
    await this.fetchStatusAndMaybeCheck();
    // Schedule next check based on nextRefresh
    this.scheduleNextCheck();
  }

  // Notify manager that logout occurred (or auth became false)
  public onLogout(): void {
    this.clearTimers();
    // Reset status to default state
    this.currentStatus = {
      isRefreshing: false,
      lastRefresh: null,
      nextRefresh: null,
      intervalMinutes: 30
    };
    this.isDuringActivePoll = false;
    // Explicit logout should not leave the manager in suspended state
    this.setSuspended(false);
  }

  // Check if user is authenticated (and admin, depending on provided function)
  private isAuthenticated(): boolean {
    if (!this.authCheckFunction) return false;
    return this.authCheckFunction();
  }

  // Whether we should suppress all activity on auth/login routes to avoid loops
  private isOnAuthRoute(): boolean {
    if (typeof window === 'undefined') return false;
    const p = window.location?.pathname || '';
    return /(\/(auth|authorization|login)(\/|$))/i.test(p);
  }

  // Persist or clear suspension flag
  private setSuspended(flag: boolean): void {
    this.isSuspendedOnAuthError = flag;
    if (typeof window !== 'undefined') {
      try {
        const key = GlobalDataRefreshManager.SUSPEND_STORAGE_KEY;
        if (flag) {
          window.sessionStorage.setItem(key, '1');
        } else {
          window.sessionStorage.removeItem(key);
        }
      } catch {
        // ignore storage errors
      }
    }
  }

  // Fetch status from server ~ do not auto-trigger refresh; backend scheduler handles it
  private async fetchStatusAndMaybeCheck(): Promise<void> {
    // Critical: check auth FIRST before making any API calls
    if (!this.isAuthenticated() || this.isSuspendedOnAuthError || this.isOnAuthRoute()) {
      this.clearTimers();
      return;
    }

    try {
      const { apiClient } = await import('@rti/shared');

      // Always get the latest status
      const serverStatus = await apiClient.getGlobalRefreshStatus();
      this.applyServerStatus(serverStatus);

      // If a refresh is currently running, begin short polling to detect completion
      if (this.currentStatus.isRefreshing) {
        this.startActiveRefreshPolling();
      }
    } catch (error: any) {
      console.error('Failed to fetch global refresh status:', error);
      // If authentication failed (401), stop all timers to prevent redirect loop
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.warn('Authentication failed for global refresh status - stopping all timers and clearing state');
        this.setSuspended(true); // prevent any further calls until onLogin
        this.clearTimers();
        // Reset status to default
        this.currentStatus = {
          isRefreshing: false,
          lastRefresh: null,
          nextRefresh: null,
          intervalMinutes: 30
        };
        this.isDuringActivePoll = false;
      }
    }
  }

  // Apply server status to local state and notify if changed
  private applyServerStatus(serverStatus: any): void {
    const newStatus: RefreshStatus = {
      isRefreshing: !!serverStatus?.is_refreshing,
      lastRefresh: serverStatus?.last_refresh ? new Date(serverStatus.last_refresh) : null,
      nextRefresh: serverStatus?.next_refresh ? new Date(serverStatus.next_refresh) : null,
      intervalMinutes: serverStatus?.interval_minutes || this.currentStatus.intervalMinutes || 30,
      initiated_by: serverStatus?.initiated_by,
    };

    const hasChanged =
      this.currentStatus.isRefreshing !== newStatus.isRefreshing ||
      (this.currentStatus.lastRefresh?.getTime() || 0) !== (newStatus.lastRefresh?.getTime() || 0) ||
      (this.currentStatus.nextRefresh?.getTime() || 0) !== (newStatus.nextRefresh?.getTime() || 0) ||
      this.currentStatus.intervalMinutes !== newStatus.intervalMinutes;

    this.currentStatus = newStatus;
    if (hasChanged) this.notifyListeners();
  }

  private startActiveRefreshPolling(): void {
    if (this.isDuringActivePoll) return; // avoid duplicate polls
    // Mark as polling BEFORE starting the interval to prevent re-entrancy
    this.isDuringActivePoll = true;

    const pollIntervalMs = 20000;
    const maxPollMs = 2 * 60 * 1000; // 2 minutes safety cap
    const startTime = Date.now();

    // Clear any existing poll timer first
    if (this.statusPollTimer) {
      clearInterval(this.statusPollTimer);
      this.statusPollTimer = null;
    }

    this.statusPollTimer = setInterval(async () => {
      if (!this.isAuthenticated() || this.isSuspendedOnAuthError || this.isOnAuthRoute()) {
        this.stopActiveRefreshPolling();
        return;
      }

      try {
        const { apiClient } = await import('@rti/shared');
        const serverStatus = await apiClient.getGlobalRefreshStatus();
        this.applyServerStatus(serverStatus);

        if (!this.currentStatus.isRefreshing) {
          // Completed
          this.stopActiveRefreshPolling();
          // Reschedule next check now that we have updated nextRefresh
          this.scheduleNextCheck();
          return;
        }

        if (Date.now() - startTime > maxPollMs) {
          console.warn('Refresh polling timed out; stopping.');
          this.stopActiveRefreshPolling();
          this.scheduleNextCheck();
        }
      } catch (error: any) {
        console.error('Error during active refresh polling:', error);
        // If authentication failed (401), stop polling immediately
        if (error?.response?.status === 401 || error?.response?.status === 403 ) {
          console.warn('Authentication failed during polling - stopping all timers and clearing state');
          this.setSuspended(true); // suspend further attempts until login
          this.stopActiveRefreshPolling();
          this.clearTimers();
          // Reset status to default
          this.currentStatus = {
            isRefreshing: false,
            lastRefresh: null,
            nextRefresh: null,
            intervalMinutes: 30
          };
          this.isDuringActivePoll = false;
          return;
        }
        // On other errors, keep trying until timeout to avoid UI stuck
        if (Date.now() - startTime > maxPollMs) {
          this.stopActiveRefreshPolling();
          this.scheduleNextCheck();
        }
      }
    }, pollIntervalMs);
  }

  private stopActiveRefreshPolling(): void {
    if (this.statusPollTimer) {
      clearInterval(this.statusPollTimer);
      this.statusPollTimer = null;
    }
    this.isDuringActivePoll = false;
  }

  // Schedule the next server check 1 minute after nextRefresh
  private scheduleNextCheck(): void {
    if (this.scheduledCheckTimer) {
      clearTimeout(this.scheduledCheckTimer);
      this.scheduledCheckTimer = null;
    }

    if (!this.isAuthenticated() || this.isSuspendedOnAuthError || this.isOnAuthRoute()) {
      return;
    }

    // Only schedule when nextRefresh is known
    if (!this.currentStatus.nextRefresh) {
      return;
    }

    const oneMinute = 60 * 1000;
    const now = Date.now();
    const next = this.currentStatus.nextRefresh.getTime();

    let delay = next + oneMinute - now;
    // If nextRefresh is in the past (or very soon), run check in 15s to avoid spam
    if (!isFinite(delay) || delay < 0) delay = 15 * 1000;

    this.scheduledCheckTimer = setTimeout(async () => {
      // Double-check auth before running
      if (!this.isAuthenticated() || this.isSuspendedOnAuthError || this.isOnAuthRoute()) {
        return;
      }
      await this.fetchStatusAndMaybeCheck();
      // After running, schedule again based on possibly updated nextRefresh
      this.scheduleNextCheck();
    }, delay);
  }

  // Manual refresh trigger - delegates to server and then polls briefly for completion
  public async triggerManualRefresh(): Promise<void> {
    if (!this.isAuthenticated()) {
      throw new Error('User must be authenticated to perform manual refresh');
    }

    try {
      const { apiClient } = await import('@rti/shared');
      const result = await apiClient.requestGlobalRefresh();

      if (result?.success === false) {
        throw new Error(result.message || 'Manual refresh failed');
      }

      // Refresh started (or already running) — start short polling to observe completion
      this.startActiveRefreshPolling();
    } catch (error) {
      console.error('Manual refresh failed:', error);
      throw error;
    }
  }

  // Admin-only: force reset global refresh state on server, then update local state
  public async reset(): Promise<void> {
    if (!this.isAuthenticated()) {
      throw new Error('User must be authenticated to reset refresh state');
    }
    try {
      const shared = await import('@rti/shared');
      const apiClient: any = (shared as any).apiClient;
      const routes: any = (shared as any).API_ROUTES;

      let result: any;
      if (typeof apiClient.resetGlobalRefresh === 'function') {
        result = await apiClient.resetGlobalRefresh();
      } else if (typeof apiClient.post === 'function') {
        const url = routes?.GLOBAL_REFRESH_RESET ?? '/gateway/global-refresh/reset';
        const resp = await apiClient.post(url);
        result = resp?.data ?? resp;
      } else {
        throw new Error('Reset endpoint not available');
      }

      const status = result?.status ?? {};
      this.applyServerStatus(status);
      this.clearTimers();
      this.scheduleNextCheck();
    } catch (error) {
      console.error('Reset refresh state failed:', error);
      throw error;
    }
  }

  // Update configuration - currently local only; server-driven config can be added later
  public updateConfig(config: {
    intervalMinutes?: number;
    skipWeekends?: boolean;
    businessHoursOnly?: boolean;
    businessHours?: { start: number; end: number };
    timezone?: string;
  }): void {
    if (config.intervalMinutes) {
      this.currentStatus.intervalMinutes = config.intervalMinutes;
    }
  }

  // Notify all listeners
  private notifyListeners(): void {
    this.listeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in refresh listener:', error);
      }
    });
  }

  private clearTimers(): void {
    if (this.statusPollTimer) {
      clearInterval(this.statusPollTimer);
      this.statusPollTimer = null;
    }
    if (this.scheduledCheckTimer) {
      clearTimeout(this.scheduledCheckTimer);
      this.scheduledCheckTimer = null;
    }
    this.isDuringActivePoll = false;
  }

  // Clean up resources (if ever needed)
  public destroy(): void {
    this.clearTimers();
    this.listeners.clear();

    // Remove auth-logout listener
    if (this.authLogoutListener && typeof window !== 'undefined') {
      window.removeEventListener('auth-logout', this.authLogoutListener);
      this.authLogoutListener = null;
    }

    GlobalDataRefreshManager.instance = null;
  }
}

export default GlobalDataRefreshManager;

